// src/pages/applications/Management.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../../components/LocalizedLink";

const ASSET = (p) => `${process.env.PUBLIC_URL || ""}${p}`;
const IMG = (f) => ASSET(`/management/${f}`);

export default function Management() {
  const { t } = useTranslation();

  // i18n structured bits
  const heroBullets =
    t("applications.detail.management.hero.bullets", { returnObjects: true }) ||
    [];

  const plans = {
    starter: t("applications.detail.management.pricing.items.starter", {
      returnObjects: true,
    }),
    standard: t("applications.detail.management.pricing.items.standard", {
      returnObjects: true,
    }),
    plus: t("applications.detail.management.pricing.items.plus", {
      returnObjects: true,
    }),
  };

  // Only team/project features now
  const features = ["projects", "tasks", "teams", "assignments", "chat", "voice"];

  const screens = [
    ["screenshot-dashboard.svg", "applications.detail.management.screens.captions.dashboard"],
    ["screenshot-projects.svg", "applications.detail.management.screens.captions.projects"],
    ["screenshot-teams.svg", "applications.detail.management.screens.captions.teams"],
    ["screenshot-chat.svg", "applications.detail.management.screens.captions.chat"],
  ];

  return (
    <main className="bg-white text-slate-900">
      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-100">
              {t("applications.detail.management.hero.badge_left")}
              <span className="inline-block size-1 rounded-full bg-indigo-300" />
              {t("applications.detail.management.hero.badge_right")}
            </span>

            <h1 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight">
              {t("applications.detail.management.hero.title")}
            </h1>
            <p className="mt-3 text-slate-600">
              {t("applications.detail.management.hero.desc")}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={IMG("cover.svg")}
                className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium hover:bg-indigo-500"
              >
                {t("applications.detail.management.hero.view_demo")}
              </a>
              <LocalizedLink
                to="/contact"
                className="rounded-xl px-5 py-3 font-medium ring-1 ring-slate-300 hover:bg-slate-50"
              >
                {t("applications.detail.management.hero.start")}
              </LocalizedLink>
            </div>

            <ul className="mt-6 grid grid-cols-2 gap-2 text-sm text-slate-600">
              {heroBullets.map((b, i) => (
                <li key={i}>• {b}</li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <img
              src={IMG("cover.svg")}
              alt={t("applications.detail.management.hero.mock_alt")}
              className="w-full rounded-xl object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.management.features.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.management.features.sub")}
          </p>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((k) => (
              <FeatureCard
                key={k}
                icon={IMG(`feature-${k}.svg`)}
                title={t(
                  `applications.detail.management.features.items.${k}.title`
                )}
                desc={t(
                  `applications.detail.management.features.items.${k}.desc`
                )}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Screens */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.management.screens.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.management.screens.sub")}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {screens.map(([src, cap]) => (
              <figure
                key={src}
                className="rounded-2xl border border-slate-200 bg-white p-3"
              >
                <img
                  src={IMG(src)}
                  alt={t(cap)}
                  className="w-full rounded-xl object-cover"
                  loading="lazy"
                />
                <figcaption className="px-2 pb-2 pt-3 text-sm text-slate-600">
                  {t(cap)}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.management.pricing.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.management.pricing.sub")}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <PriceCard
              name={plans.starter.name}
              price={plans.starter.price}
              bullets={plans.starter.bullets || []}
            />
            <PriceCard
              featured
              name={plans.standard.name}
              price={plans.standard.price}
              bullets={plans.standard.bullets || []}
            />
            <PriceCard
              name={plans.plus.name}
              price={plans.plus.price}
              bullets={plans.plus.bullets || []}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-indigo-600 text-white">
        <div className="mx-auto max-w-7xl px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t("applications.detail.management.cta.title")}
          </h2>
          <p className="mt-2 text-indigo-100">
            {t("applications.detail.management.cta.sub")}
          </p>
          <div className="mt-6">
            <LocalizedLink
              to="/contact"
              className="inline-block rounded-xl bg-white px-6 py-3 font-medium text-indigo-700 hover:bg-indigo-50"
            >
              {t("applications.detail.management.cta.start")}
            </LocalizedLink>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ---------- Small presentational components ---------- */

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-6 bg-white">
      <img src={icon} alt="" className="h-8 w-8" loading="lazy" />
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-slate-600">{desc}</p>
    </div>
  );
}

function PriceCard({ name, price, bullets, featured }) {
  return (
    <div
      className={
        "rounded-2xl border p-6 " +
        (featured
          ? "border-indigo-200 bg-indigo-50 shadow-md"
          : "border-slate-200 bg-white")
      }
    >
      <div className="text-sm uppercase tracking-wide text-slate-500">
        {name}
      </div>
      <div className="mt-2 text-3xl font-bold">{price}</div>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {(bullets || []).map((b, i) => (
          <li key={i}>• {b}</li>
        ))}
      </ul>
    </div>
  );
}
